<script>
    // Home for someone who has been here before. The two jobs a returning
    // user comes back for lead: check progress, and check a product. The
    // routine and its rating sit underneath, re-rated on every visit so the
    // number reflects today's profile and today's routine.
    import { onMount } from 'svelte';
    import { navigate } from '../router.js';
    import { userStore } from '../store.js';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';

    export let goToFaceAnalysis;

    let lastPhoto = null;
    let photoCount = 0;
    let routine = [];
    let rating = null;
    let loaded = false;

    onMount(async () => {
        await Promise.all([loadProgress(), loadRoutine()]);
        loaded = true;
        if (routine.length) rateRoutine();
    });

    async function loadProgress() {
        try {
            const r = await fetch('/api/progress', { credentials: 'include' });
            if (!r.ok) return;
            const data = await r.json();
            const entries = (data.entries || []).filter(e => e.date);
            photoCount = entries.length;
            lastPhoto = entries.length ? new Date(entries[0].date) : null;
        } catch (e) {
            console.error('Progress load failed:', e);
        }
    }

    async function loadRoutine() {
        try {
            const r = await fetch('/api/current-routine', { credentials: 'include' });
            if (!r.ok) return;
            const data = await r.json();
            routine = data.items || [];
            if (routine.length) localStorage.setItem('currentRoutine', JSON.stringify(routine));
        } catch (e) {
            console.error('Routine load failed:', e);
        }
    }

    async function rateRoutine() {
        try {
            const r = await fetch('/api/routine-analyze', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ items: routine, userId: $userStore.userId })
            });
            if (!r.ok) return;
            rating = await r.json();
            localStorage.setItem('routineRating', JSON.stringify({
                score: rating.score,
                verdict: rating.verdict || rating.nextAction || '',
                fixes: (rating.fixes || []).map(f => f.title),
                ratedAt: new Date().toISOString()
            }));
        } catch (e) {
            console.error('Routine rating failed:', e);
        }
    }

    $: daysSincePhoto = lastPhoto ? Math.floor((Date.now() - lastPhoto.getTime()) / 86400000) : null;
    $: firstName = ($userStore.username || '').split(/\s+/)[0];
    $: photoPrompt = daysSincePhoto === null
        ? 'Take your first photo so SkinGenie has something to compare against.'
        : daysSincePhoto >= 14
            ? `Your last photo was ${daysSincePhoto} days ago. A new one shows what has changed.`
            : `Your last photo was ${daysSincePhoto === 0 ? 'today' : daysSincePhoto === 1 ? 'yesterday' : daysSincePhoto + ' days ago'}. Every two to four weeks is enough to see change.`;
    $: topFix = rating && rating.fixes && rating.fixes.length ? rating.fixes[0] : null;
</script>

<div class="u-page home">
    <header>
        <p class="eyebrow">Welcome back</p>
        <h1>{firstName ? `Hi ${firstName}.` : 'Hi again.'} What are we checking today?</h1>
    </header>

    <div class="main">
        <Card>
            <h2>Check my progress</h2>
            <p class="muted">{photoPrompt}</p>
            <div class="actions">
                <Button on:click={goToFaceAnalysis}>Take a new photo</Button>
                {#if photoCount > 1}
                    <Button variant="secondary" on:click={() => navigate('progress')}>See my progress</Button>
                {/if}
            </div>
        </Card>

        <Card>
            <h2>Check a product</h2>
            <p class="muted">Scan it or search for it. SkinGenie tells you whether it suits your skin and fits your routine.</p>
            <div class="actions">
                <Button on:click={() => navigate('scanner')}>Check a product</Button>
            </div>
        </Card>
    </div>

    <section class="routine" aria-labelledby="routine-title">
        <h2 id="routine-title">My routine</h2>
        {#if !loaded}
            <p class="muted">Loading your routine…</p>
        {:else if routine.length === 0}
            <p class="muted">You haven’t added your routine yet. Add it and SkinGenie will rate it for your skin.</p>
            <Button variant="secondary" on:click={() => navigate('routine-import')}>Add my routine</Button>
        {:else}
            <div class="routine-row">
                {#if rating}
                    <div class="score"><span class="num">{rating.score}</span><span class="of">/100</span></div>
                {/if}
                <div class="routine-copy">
                    <p class="verdict">{rating ? (rating.verdict || rating.nextAction) : `${routine.length} products`}</p>
                    {#if topFix}
                        <p class="muted">First change: {topFix.title}.</p>
                    {/if}
                </div>
            </div>
            <div class="actions">
                <Button variant="secondary" on:click={() => navigate('routine-import')}>See the full rating</Button>
                <Button variant="ghost" on:click={() => navigate('next-step')}>Find products for it</Button>
            </div>
        {/if}
    </section>
</div>

<style>
    .home { font-family: var(--font-body); }
    header { max-width: var(--prose-max); margin-bottom: var(--space-10); }
    .eyebrow {
        color: var(--sage);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
    }
    h1 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        font-size: var(--text-4xl);
        line-height: var(--leading-tight);
        margin: var(--space-3) 0 0;
    }
    h2 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        font-size: var(--text-xl);
        margin: 0 0 var(--space-2);
    }
    .main {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: var(--space-4);
    }
    .muted {
        color: var(--text-muted);
        line-height: var(--leading-relaxed);
        margin: 0 0 var(--space-5);
    }
    .actions { display: flex; flex-wrap: wrap; gap: var(--space-3); }
    .routine {
        margin-top: var(--section-gap);
        padding-top: var(--space-10);
        border-top: 1px solid var(--border);
    }
    .routine-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--space-6);
        margin: var(--space-4) 0 var(--space-6);
    }
    .score { font-family: var(--font-display); color: var(--sage); line-height: var(--leading-tight); }
    .num { font-size: var(--text-4xl); }
    .of { font-size: var(--text-md); color: var(--text-muted); margin-left: var(--space-1); }
    .routine-copy { flex: 1 1 260px; }
    .verdict { color: var(--text); font-size: var(--text-lg); margin: 0 0 var(--space-2); }
</style>
